import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import { companyInfo, privacySections } from './privacyContent';

import css from './PrivacyPolicyPage.module.css';

import { ReactComponent as DownloadIcon } from '../../assets/svg/downloadIcon.svg';

const renderContent = value => {
  if (typeof value !== 'string') {
    return value;
  }

  return value.startsWith('PrivacyPolicyPage.') ? <FormattedMessage id={value} /> : value;
};

const Paragraph = ({ id, text }) => (
  <p className={css.paragraph}>{id ? renderContent(id) : text}</p>
);

// Bolds the lead-in before the first colon, e.g. "MettaUniverse: Our online
// marketplace" -> **MettaUniverse:** Our online marketplace.
const BoldLeadText = ({ text }) => {
  const colonIndex = text.indexOf(':');
  if (colonIndex === -1) {
    return text;
  }

  return (
    <>
      <span className={css.boldLead}>{text.slice(0, colonIndex + 1)}</span>
      {text.slice(colonIndex + 1)}
    </>
  );
};

const renderListItem = item => {
  if (typeof item !== 'string') {
    return item;
  }

  return item.startsWith('PrivacyPolicyPage.') ? (
    <FormattedMessage id={item}>{chunks => <BoldLeadText text={chunks.join('')} />}</FormattedMessage>
  ) : (
    <BoldLeadText text={item} />
  );
};

const BulletList = ({ items, itemIds }) => {
  const listItems = items || itemIds || [];

  return (
    <ul className={css.bulletList}>
      {listItems.map((item, index) => (
        <li key={typeof item === 'string' ? item : index}>{renderListItem(item)}</li>
      ))}
    </ul>
  );
};

const OrderedList = ({ itemIds }) => (
  <ol className={css.orderedList}>
    {itemIds.map((item, index) => (
      <li key={typeof item === 'string' ? item : index}>{renderListItem(item)}</li>
    ))}
  </ol>
);

const KeyValueTable = ({ rows }) => (
  <div className={css.tableWrap}>
    <table className={css.table}>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            <td className={css.tableLabelCell}>{renderContent(row[0])}</td>
            <td>{renderContent(row[1])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Box = ({ title, titleId, body, bodyId }) => (
  <div className={css.box}>
    {title || titleId ? <p className={css.boxTitle}>{renderContent(title || titleId)}</p> : null}
    <Paragraph id={bodyId} text={body} />
  </div>
);

const Callout = ({ titleId, textId }) => (
  <div className={css.callout}>
    {titleId ? <h3 className={css.calloutTitle}>{renderContent(titleId)}</h3> : null}
    <Paragraph id={textId} />
  </div>
);

const ContactSection = ({ cards }) => (
  <div className={css.contactGrid}>
    {cards.map(card => (
      <div className={css.contactCard} key={card.headingId}>
        <p className={css.contactCardHeading}>{renderContent(card.headingId)}</p>
        {card.rows.map((row, index) => (
          <div className={css.contactRow} key={index}>
            {row.labelId ? (
              <span className={css.contactLabel}>{renderContent(row.labelId)}</span>
            ) : null}
            <span className={css.contactValue}>{row.value}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <Paragraph id={block.id} />;
    case 'ul':
      return <BulletList itemIds={block.itemIds} />;
    case 'ol':
      return <OrderedList itemIds={block.itemIds} />;
    case 'box':
      return (
        <Box title={block.title} titleId={block.titleId} body={block.body} bodyId={block.bodyId} />
      );
    case 'kvTable':
      return <KeyValueTable rows={block.rows} />;
    case 'callout':
      return <Callout titleId={block.titleId} textId={block.textId} />;
    case 'contact':
      return <ContactSection cards={block.cards} />;
    default:
      return null;
  }
};

const Section = ({ section }) => {
  const content = (
    <>
      <h2 className={css.sectionHeading}>
        {section.number}. {renderContent(section.titleId)}
      </h2>
      {section.blocks.map((block, index) => (
        <Block block={block} key={index} />
      ))}
    </>
  );

  return (
    <div className={css.section}>
      {section.highlight ? (
        <div className={css.highlightBlock}>{content}</div>
      ) : section.gradient ? (
        <div className={css.gradientBlock}>{content}</div>
      ) : section.accentBar ? (
        <div className={css.accentBarBlock}>{content}</div>
      ) : (
        content
      )}
    </div>
  );
};

/**
 * Content-only rendering of the Privacy Policy, meant for embedding
 * elsewhere on the site (e.g. inside the sign-up modal).
 */
export const PrivacyPolicyContent = () => {
  const contactIndex = privacySections.findIndex(section => section.number === '15');
  const cardSections =
    contactIndex === -1 ? privacySections : privacySections.slice(0, contactIndex);
  const afterCardSections = contactIndex === -1 ? [] : privacySections.slice(contactIndex);

  return (
    <>
      <div className={css.card}>
        {cardSections.map(section => (
          <Section section={section} key={section.number} />
        ))}
      </div>
      {afterCardSections.length > 0 ? (
        <div className={css.cardFooter}>
          {afterCardSections.map(section => (
            <Section section={section} key={section.number} />
          ))}
        </div>
      ) : null}
    </>
  );
};

export const PrivacyPolicyPageComponent = props => {
  const { scrollingDisabled } = props;
  const history = useHistory();

  const handleDownloadPdf = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  return (
    <Page title="Privacy Policy" scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.heroSection}>
            <div className={css.topRow}>
              <button className={css.backLink} onClick={() => history.goBack()} type="button">
                <FormattedMessage id="PrivacyPolicyPage.backBtn" />
              </button>
              <button className={css.downloadButton} onClick={handleDownloadPdf} type="button">
                <DownloadIcon className={css.icon} />
                <FormattedMessage id="PrivacyPolicyPage.downloadBtn" />
              </button>
            </div>

            <h1 className={css.title}>
              <FormattedMessage id="PrivacyPolicyPage.title" />
            </h1>
            <div className={css.subtitleRow}>
              <span>{companyInfo.name}</span>
              <span className={css.subtitleDivider}>|</span>
              <span>
                <FormattedMessage id={companyInfo.registrationLabelId} /> -{' '}
                <span className={css.registrationNumber}>{companyInfo.registrationNumber}</span>
              </span>
            </div>
          </div>

          <div className={css.content}>
            <PrivacyPolicyContent />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const PrivacyPolicyPage = connect(mapStateToProps)(PrivacyPolicyPageComponent);

export default PrivacyPolicyPage;
